/**
 * Defensive DOM patches for the web export. Applied once at startup
 * (side-effect import at the top of `_layout.web.tsx`).
 *
 * 1. Google Translate (and similar in-page translators) replaces text nodes
 *    with <font> wrappers, desyncing React's virtual DOM from the real DOM.
 *    React then crashes with NotFoundError on `insertBefore`/`removeChild`.
 *    The React team's endorsed workaround is to make those two calls fail
 *    soft when the reference/child node was moved by the translator.
 *    See https://github.com/facebook/react/issues/11538#issuecomment-417504600
 *
 * 2. @floating-ui/dom's autoUpdate (pulled in via assistant-ui → Radix
 *    tooltips) calls IntersectionObserver.observe() without checking the
 *    target is still an Element, throwing unhandled-rejection TypeErrors
 *    when a tooltip's reference unmounts mid-update. Guard `observe` to
 *    ignore non-Element targets instead of throwing.
 */

let applied = false;

export function applyDomSafetyPatches() {
  if (applied || typeof window === 'undefined' || typeof Node === 'undefined') {
    return;
  }
  applied = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChildSafe<T extends Node>(
    this: Node,
    child: T,
  ): T {
    if (child.parentNode !== this) {
      if (typeof console !== 'undefined') {
        console.warn(
          '[dom-safety] Skipped removeChild for a node that is not a child of this parent (likely mutated by a page translator).',
        );
      }
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBeforeSafe<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (typeof console !== 'undefined') {
        console.warn(
          '[dom-safety] Skipped insertBefore for a reference node that is not a child of this parent (likely mutated by a page translator).',
        );
      }
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };

  if (
    typeof IntersectionObserver !== 'undefined' &&
    typeof Element !== 'undefined'
  ) {
    const originalObserve = IntersectionObserver.prototype.observe;
    IntersectionObserver.prototype.observe = function observeSafe(
      this: IntersectionObserver,
      target: Element,
    ) {
      if (!(target instanceof Element)) {
        if (typeof console !== 'undefined') {
          console.warn(
            '[dom-safety] Ignored IntersectionObserver.observe on a non-Element target.',
          );
        }
        return;
      }
      originalObserve.call(this, target);
    };
  }
}

applyDomSafetyPatches();
